import {
  Box,
  Card,
  CardActions,
  CardHeader,
  Divider,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import React, { ChangeEvent, useEffect, useMemo, useState } from "react";
import { Button } from "react-bootstrap";
import "react-image-crop/dist/ReactCrop.css";
import { useSelector } from "react-redux";
import { useCategories, useCategoryById } from "../hooks";
import styles from "../styles/productAddProductModal.module.css";
import { BrandInstance } from "../utils/brandClass";
import { ProductInstance } from "../utils/productClass";
import { ImageScaleAndCropComponent } from "./imageScaleAndCropComponent";
interface ProductAddProductModalProps {
  onClose: () => void;
  product: any;
  isEditing: boolean;
}

interface ProductForm {
  name: string;
  description: string;
  categoryId: number;
  subCategoryId: number | null;
  thcContent: string | null;
  cbdContent: string | null;
  cbnContent: string | null;
  strain: string | null;
}

const initialState: ProductForm = {
  name: "",
  description: "",
  categoryId: 1, // Assuming IDs are strings. Adjust accordingly.
  subCategoryId: 1,
  thcContent: "0",
  cbdContent: "0",
  cbnContent: "0",
  strain: "",
};

const STRAINS = ["Sativa", "Indica", "Hybrid", "CBD"];

// WHY THE FUCK IS THIS CALLED A MODAL WHEN WE'RE LOADING IT DIRECTLY AS A PAGE ?!?
export const ProductAddProductModal: React.FC<ProductAddProductModalProps> = ({
  onClose,
  product = null,
  isEditing = false,
}: {
  onClose: any;
  product?: any;
  isEditing?: boolean;
}) => {
  const { activeBusiness } = useSelector((state: any) => state.business);
  const [selectedThumbnail, setSelectedThumbnail] = useState<File | null>(null);
  const [selectedDisplayImage, setSelectedDisplayImage] = useState<File | null>(null);
  const [formComplete, setFormComplete] = useState(false);
  const [formData, setFormData] = useState<ProductForm>(initialState);
  const [originalFormData, setOriginalFormData] = useState<ProductForm>(initialState);
  const [isCannabis, setIsCannabis] = useState(true);

  const categories = useCategories();
  const { category, subCategories } = useCategoryById(formData?.categoryId);

  // This is used to determine if we show % or mg for THC, CBD, and CBN content, we should consider adding this as a flag on categories.
  const categoryLabel = useMemo(() => category?.name, [category]);

  const [isCropVisible, setIsCropVisible] = useState<{
    thumb: boolean;
    display: boolean;
  }>({
    thumb: false,
    display: false,
  });
  const [imageSrc, setImageSrc] = useState<{
    thumb: string | null;
    display: string | null;
  }>({
    thumb: null,
    display: null,
  });
  const [canSave, setCanSave] = useState(false);

  //whenever subCategories changes, and if sativa, indica, hybrid, or cbd chosen, set strain
  useEffect(() => {
    const selectedSubCategory = subCategories.find((subCategory: any) => subCategory.id === formData.subCategoryId);

    if (selectedSubCategory && STRAINS.includes(selectedSubCategory.name)) {
      setFormData({
        ...formData,
        strain: selectedSubCategory.name,
      });
    }
  }, [formData.subCategoryId]);

  //initializing form and originalFormData for comparison
  useEffect(() => {
    if (isEditing && product) {
      (async () => {
        //for editing in form
        setFormData({
          name: product.name,
          description: product.description,
          categoryId: product.category.id,
          subCategoryId: product.subCategoryId,
          thcContent: product.thcContent,
          cbdContent: product.cbdContent,
          cbnContent: product.cbnContent,
          strain: product.strain,
        });
        //for comparison
        setOriginalFormData({
          name: product.name,
          description: product.description,
          categoryId: product.category.id,
          subCategoryId: product.subCategoryId,
          thcContent: product.thcContent,
          cbdContent: product.cbdContent,
          cbnContent: product.cbnContent,
          strain: product.strain,
        });
        setImageSrc({
          thumb: product.avatarUrl,
          display: product.displayUrl,
        });

        await ProductInstance.getProductSubCategories(product.category.id);
      })();
    } else {
      setFormData(initialState);
    }
  }, [isEditing, product]);

  useEffect(() => {
    if (isEditing && product) {
      const categoryIndex = categories.findIndex((category: any) => category.id === product.category.id);
      if (categoryIndex !== -1) {
        // Ensure category is found
        setFormData({
          ...formData,
          categoryId: categoryIndex,
          // Other fields remain unchanged
        });
        setOriginalFormData({
          ...formData,
          categoryId: categoryIndex,
          // Other fields remain unchanged
        });
      }
      setImageSrc({
        thumb: product.avatarUrl,
        display: product.displayUrl,
      });
    }
  }, [product]);

  // useEffect to check if any changes were made to the form
  useEffect(() => {
    // if any formData or images have been changed then we need to enable the save button
    if (JSON.stringify(formData) !== JSON.stringify(originalFormData) || selectedThumbnail || selectedDisplayImage) {
      setCanSave(true);
    } else {
      setCanSave(false);
    }
  }, [formData, selectedThumbnail, selectedDisplayImage]);

  useEffect(() => {
    const isFormComplete = Object.values(formData).every((value) => {
      // Convert value to string if it's not already a string
      const stringValue = typeof value === "string" ? value : String(value);
      return stringValue.trim() !== "";
    });

    // Set formComplete based on whether the form is complete or not
    setFormComplete(isFormComplete);
  }, [formData, selectedThumbnail, selectedDisplayImage]);

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  useEffect(() => setFormData((prev) => ({ ...prev, subCategoryId: null })), [formData.categoryId]);

  // TODO: Either burn all of this with fire or refactor it into a shared image cropping component
  const handleImageCropped = async (croppedImageUrl: string, type: "thumb" | "display") => {
    // Convert the cropped image URL to a Blob
    const response = await fetch(croppedImageUrl);
    const blob = await response.blob();

    // Create a File object from the Blob
    const fileExt = blob.type.split("/")[1]; // Extracts the file extension from the MIME type
    const fileName = `cropped_image.${fileExt}`; // Constructs a file name
    const croppedImageFile = new File([blob], fileName, { type: blob.type });

    // Generate a URL for the cropped image file
    const croppedImageURL = URL.createObjectURL(croppedImageFile);

    // Update the appropriate state with the new File object
    if (type === "thumb") {
      setSelectedThumbnail(croppedImageFile);
      setImageSrc((prev) => ({ ...prev, thumb: croppedImageURL }));
    } else if (type === "display") {
      setSelectedDisplayImage(croppedImageFile);
      setImageSrc((prev) => ({ ...prev, display: croppedImageURL }));
    }

    // Hide the cropping modal
    setIsCropVisible((prev) => ({ ...prev, [type]: false }));
  };

  const handleThumbnailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleImageChange(event, "thumb");
  };

  const handleDisplayImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleImageChange(event, "display");
  };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>, type: "thumb" | "display") => {
    const file = event.target.files ? event.target.files[0] : null;
    if (file) {
      const fileURL = URL.createObjectURL(file);
      setImageSrc((prev) => ({ ...prev, [type]: fileURL }));
      setIsCropVisible((prev) => ({ ...prev, [type]: true }));
    }
  };

  const handleSubmitProduct = async () => {
    const data = {
      product: {
        ...formData,
        ...(!isCannabis
          ? {
              thcContent: null,
              cbdContent: null,
              cbnContent: null,
              strain: null,
            }
          : {}),
      },
      brandId: activeBusiness.id,
      displayImg: selectedDisplayImage,
      avatarImg: selectedThumbnail,
    };

    if (isEditing) {
      // compare the formData with originalFormData to see if any changes were made
      const changedData = Object.keys({
        ...formData,
        ...(!isCannabis && {
          thcContent: null,
          cbdContent: null,
          cbnContent: null,
          strain: null,
        }),
      }).reduce((acc: any, key: any) => {
        const formKey = key as keyof ProductForm;
        if (formData[formKey] !== originalFormData[formKey]) {
          acc[formKey] = formData[formKey];
        }
        return acc;
      }, {} as Partial<ProductForm>);

      const updateRes = await BrandInstance.updateProduct({
        id: product.id,
        data: {
          name: formData.name,
          ...changedData,
        },
        avatarImg: selectedThumbnail,
        displayImg: selectedDisplayImage,
      });
      if (updateRes) {
        onClose();
      } else {
        console.error("Product not updated");
      }
    } else {
      const addRes = await BrandInstance.addProduct(data);

      if (addRes) {
        onClose();
      }
    }
  };

  return (
    <Card elevation={0}>
      <>
        {isCropVisible.thumb && (
          <ImageScaleAndCropComponent
            setSrc={(croppedImgUrl: any) => {
              handleImageCropped(croppedImgUrl, "thumb");
            }}
            imageType="avatar"
            src={imageSrc.thumb}
            handleClose={() => setIsCropVisible((prev) => ({ ...prev, thumb: false }))}
          />
        )}
        {isCropVisible.display && (
          <ImageScaleAndCropComponent
            setSrc={(croppedImgUrl: any) => {
              handleImageCropped(croppedImgUrl, "display");
            }}
            imageType="displayImage"
            src={imageSrc.display}
            handleClose={() => setIsCropVisible((prev) => ({ ...prev, display: false }))}
          />
        )}
      </>
      {/* HEADER */}
      <CardHeader
        title={isEditing ? "Edit Product" : "Add Product"}
        subheader={
          isEditing ? "Edit your product details" : "Fill out the form below to add a new product to your brand"
        }
      />
      {/* LEFT FORM BAR */}
      <Grid container spacing={2}>
        <Grid item xs={4}>
          <div className={styles.thumbnailWrapper}>
            <h4 className={styles.innerHead}>Avatar Image</h4>
            <div
              style={{
                margin: "0 auto 10px auto",
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              {imageSrc.thumb ? (
                <img
                  src={
                    imageSrc.thumb
                      ? imageSrc.thumb
                      : selectedThumbnail
                        ? URL.createObjectURL(selectedThumbnail)
                        : "https://via.placeholder.com/150"
                  }
                  alt="thumbnail"
                />
              ) : (
                <div className={styles.imagePlaceHolder}>
                  <h3>600 x 600</h3>
                </div>
              )}
            </div>
            <div style={{ display: "flex" }}>
              <input type="file" onChange={handleThumbnailChange} accept="image/png, image/jpeg, image/jpg" />
            </div>
            <p>
              Set the product thumbnail image. Please select an image that is 600 x 600. Only *.png, *.jpg and *.jpeg
              image files are accepted
            </p>
          </div>

          <div className={styles.thumbnailWrapper}>
            <h4 className={styles.innerHead}>Display Image</h4>
            <div
              style={{
                margin: "0 auto 10px auto",
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              {imageSrc.display ? (
                <img
                  src={
                    imageSrc.display
                      ? imageSrc.display
                      : selectedDisplayImage
                        ? URL.createObjectURL(selectedDisplayImage)
                        : "https://via.placeholder.com/150"
                  }
                  alt="display image"
                />
              ) : (
                <div className={styles.imagePlaceHolder}>
                  <h3>1080 x 1080</h3>
                </div>
              )}
            </div>
            <div style={{ display: "flex" }}>
              <input type="file" onChange={handleDisplayImageChange} accept="image/png, image/jpeg, image/jpg" />
            </div>
            <p>
              Set the Product Display image. Please select an image that is 1080 x 1080. Only *.png, *.jpg and *.jpeg
              image files are accepted
            </p>
          </div>
        </Grid>

        {/* RIGHT SIDE OF FORM */}
        <Grid item xs={8}>
          <div className={styles.borderWrapper}>
            <Typography variant="h6">Product Information</Typography>
            <Divider color="grey.400" sx={{ my: 2 }} />
            {/* NAME */}
            <TextField
              label="Product Name"
              sx={{ my: 1 }}
              name="name"
              fullWidth
              value={formData.name}
              onChange={handleChange}
              helperText="A product name is required and recommended to be unique"
            />

            {/* DESCRIPTION */}
            <TextField
              sx={{ my: 1 }}
              label="Description"
              fullWidth
              name="description"
              value={formData.description}
              onChange={handleChange}
              helperText="Set a description for your product for better understanding"
              multiline
              minRows={4}
            />
            {/* CATEGORY */}
            <FormControl fullWidth sx={{ my: 1 }}>
              <InputLabel id="category-select-label">Category</InputLabel>
              <Select
                labelId="category-select-label"
                value={formData?.categoryId ?? ""}
                label="Category"
                name="categoryId"
                onChange={handleChange as any}
              >
                {categories.map((category: any) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* SUBCATEGORY */}
            <FormControl fullWidth sx={{ my: 1 }}>
              <InputLabel id="subcategory-select-label">Sub-Category</InputLabel>
              <Select
                labelId="subcategory-select-label"
                value={formData?.subCategoryId ?? ""}
                label="Sub-Category"
                name="subCategoryId"
                onChange={handleChange as any}
              >
                {subCategories.map((sub: any) => (
                  <MenuItem key={sub.id} value={sub.id}>
                    {sub.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Divider color="grey.400" sx={{ my: 2 }} />
            <ToggleButtonGroup
              sx={{ my: 1 }}
              color="primary"
              value={isCannabis ? 1 : 0}
              exclusive
              // @ts-ignore
              onChange={(e) => setIsCannabis(!!parseInt(e?.target?.value))}
              fullWidth
            >
              <ToggleButton value={1}>Cannabis Product</ToggleButton>
              <ToggleButton value={0}>Non-Cannabis Product</ToggleButton>
            </ToggleButtonGroup>

            {/* THC CBD and CBN content inputs */}
            {isCannabis && (
              <>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    py: 1,
                    gap: 1,
                  }}
                >
                  <FormControl sx={{ flex: 1 }} variant="outlined">
                    <InputLabel htmlFor="thc-label">THC Content</InputLabel>
                    <OutlinedInput
                      id="thc-label"
                      label="THC Content"
                      endAdornment={
                        <InputAdornment position="end">{categoryLabel === "Edibles" ? "mg" : "%"}</InputAdornment>
                      }
                      value={formData?.thcContent ?? ""}
                      name="thcContent"
                      onChange={handleChange as any}
                    />
                  </FormControl>
                  <FormControl sx={{ flex: 1 }} variant="outlined">
                    <InputLabel htmlFor="cbd-label">CBD Content</InputLabel>
                    <OutlinedInput
                      id="cbd-label"
                      label="CBD Content"
                      endAdornment={
                        <InputAdornment position="end">{categoryLabel === "Edibles" ? "mg" : "%"}</InputAdornment>
                      }
                      value={formData?.cbdContent ?? ""}
                      name="cbdContent"
                      onChange={handleChange as any}
                    />
                  </FormControl>
                  <FormControl sx={{ flex: 1 }} variant="filled">
                    <InputLabel htmlFor="cbn-label">CBN Content</InputLabel>
                    <OutlinedInput
                      id="cbn-label"
                      label="CBN Content"
                      endAdornment={
                        <InputAdornment position="end">{categoryLabel === "Edibles" ? "mg" : "%"}</InputAdornment>
                      }
                      value={formData?.cbnContent ?? ""}
                      name="cbnContent"
                      onChange={handleChange as any}
                    />
                  </FormControl>
                </Box>
                {/* STRAIN */}
                <ToggleButtonGroup
                  sx={{ my: 1 }}
                  color="primary"
                  value={formData.strain}
                  exclusive
                  onChange={handleChange as any}
                  aria-label="Platform"
                  fullWidth
                >
                  {STRAINS.map((strain) => (
                    <ToggleButton value={strain}>{strain}</ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </>
            )}

            <CardActions sx={{ justifyContent: "flex-end" }}>
              <Button onClick={onClose} variant="text">
                Cancel
              </Button>
              <Button disabled={!canSave} onClick={handleSubmitProduct}>
                Save
              </Button>
            </CardActions>
          </div>
        </Grid>
      </Grid>
    </Card>
  );
};
